/**
 * *Getting started* (`/developers/docs/getting-started`) and *Migrating from monday.com*
 * (`/developers/docs/migrating`) — `specs/M8-P4-developers-docs-playground.md` §6.
 *
 * Both are the api's block model rendered by `GuideView`, so the words are the same ones
 * `reference.md` and `llms.txt` carry. The page adds only the chrome: the tab strip, the four
 * downloads and — for an admin — the *Public docs* switch.
 */
import { useQuery } from '@tanstack/react-query';
import type * as React from 'react';
import { DeveloperShell } from '../developer';
import { GuideView } from './blocks';
import { DocsTabs, DownloadsBar, PublicDocsToggle, useDevelopersSource } from './chrome';
import type { DevelopersSource } from './source';
import { useTrpcDevelopersSource } from './trpc-source';
import './developers.css';

export interface GuidePageProps {
  /** `getting-started` or `migrating`. */
  guideId: string;
  source?: DevelopersSource;
}

export function GuidePage({ guideId, source: injected }: GuidePageProps): React.ReactElement {
  const trpcSource = useTrpcDevelopersSource();
  const source = useDevelopersSource(injected, trpcSource);
  const overview = useQuery({ queryKey: ['developers', 'overview'], queryFn: () => source.overview() });
  const guides = useQuery({ queryKey: ['developers', 'guides'], queryFn: () => source.guides() });
  const guide = guides.data?.find((candidate) => candidate.id === guideId);

  return (
    <DeveloperShell current="docs">
      <div className="ez-docs" data-testid={`developers-guide-page-${guideId}`}>
        <div className="ez-docs__head">
          <DocsTabs current={guideId} />
          <div className="ez-docs__downloads">
            {overview.data ? <DownloadsBar overview={overview.data} /> : null}
          </div>
        </div>

        {guide ? (
          <GuideView guide={guide} />
        ) : (
          <p className="ez-docs__prose">{guides.isPending ? 'Loading…' : 'That page is not available.'}</p>
        )}

        {overview.data ? <PublicDocsToggle overview={overview.data} source={source} /> : null}
      </div>
    </DeveloperShell>
  );
}
